import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Settings, GripVertical, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react'
import { StandardColumn } from './types'
import { ColumnsPreference } from '../../contexts/UserPreferencesContext'
import { resolveColumnOrder, resolveColumnLabel } from './columnUtils'
import {
  ColumnsWrapper,
  ColumnsButton,
  ColumnsPopover,
  PopoverHeader,
  PopoverTitle,
  RestoreButton,
  ColumnsSearchInput,
  ColumnsList,
  ColumnRow,
  DragHandle,
  TreeConnector,
  ColumnCheckbox,
  ColumnName,
  ArrowGroup,
  ArrowButton,
} from './styles'

interface ColumnSettingsProps<T> {
  columns: StandardColumn<T>[]
  columnsState: ColumnsPreference
  onChange: (next: ColumnsPreference) => void
  onRestore: () => void
  iconOnly?: boolean
}

function ColumnSettings<T>({ columns, columnsState, onChange, onRestore, iconOnly }: ColumnSettingsProps<T>) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({})
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)

  const byId = useMemo(() => new Map(columns.map((col) => [col.id, col])), [columns])
  const fullOrder = useMemo(() => resolveColumnOrder(columns, columnsState), [columns, columnsState])
  const hiddenSet = useMemo(() => new Set(columnsState.hidden ?? []), [columnsState])
  const childrenByParent = useMemo(() => {
    const map = new Map<string, StandardColumn<T>[]>()
    columns.forEach((col) => {
      if (!col.parentId) return
      const siblings = map.get(col.parentId) ?? []
      siblings.push(col)
      map.set(col.parentId, siblings)
    })
    return map
  }, [columns])
  const topLevelOrder = useMemo(
    () => fullOrder.filter((id) => !byId.get(id)?.parentId),
    [fullOrder, byId],
  )

  useEffect(() => {
    if (!isOpen) return
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node
      if (wrapperRef.current?.contains(target)) return
      if (popoverRef.current?.contains(target)) return
      setIsOpen(false)
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect()
      if (!rect) return
      setPopoverStyle({
        position: 'fixed',
        top: rect.bottom + 6,
        right: Math.max(8, window.innerWidth - rect.right),
      })
    }
    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen])

  const isSearching = searchTerm.trim().length > 0

  const visibleRows = (isSearching ? fullOrder : topLevelOrder)
    .map((id) => ({ id, index: topLevelOrder.indexOf(id), column: byId.get(id)! }))
    .filter((row) => {
      if (!isSearching) return true
      return resolveColumnLabel(row.column).toLowerCase().includes(searchTerm.trim().toLowerCase())
    })

  const toggleColumn = (id: string) => {
    const nextHidden = hiddenSet.has(id)
      ? (columnsState.hidden ?? []).filter((hiddenId) => hiddenId !== id)
      : [...(columnsState.hidden ?? []), id]
    onChange({ order: topLevelOrder, hidden: nextHidden })
  }

  const moveColumn = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= topLevelOrder.length || fromIndex === toIndex) return
    const nextOrder = [...topLevelOrder]
    const [moved] = nextOrder.splice(fromIndex, 1)
    nextOrder.splice(toIndex, 0, moved)
    onChange({ order: nextOrder, hidden: columnsState.hidden ?? [] })
  }

  const handleDrop = (targetIndex: number) => {
    if (dragIndex === null) return
    moveColumn(dragIndex, targetIndex)
    setDragIndex(null)
  }

  const handleRestore = () => {
    onRestore()
    setSearchTerm('')
  }

  return (
    <ColumnsWrapper ref={wrapperRef}>
      <ColumnsButton
        ref={buttonRef}
        type="button"
        $iconOnly={iconOnly}
        onClick={() => setIsOpen((prev) => !prev)}
        title="Seleção & Ordem das Colunas"
      >
        <Settings size={16} />
        {!iconOnly && 'Colunas'}
      </ColumnsButton>
      {isOpen &&
        createPortal(
          <ColumnsPopover ref={popoverRef} style={popoverStyle}>
            <PopoverHeader>
              <PopoverTitle>Seleção &amp; Ordem das Colunas</PopoverTitle>
              <RestoreButton type="button" onClick={handleRestore}>
                <RotateCcw size={13} />
                Restaurar
              </RestoreButton>
            </PopoverHeader>
            <ColumnsSearchInput
              placeholder="Buscar coluna..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            <ColumnsList>
              {visibleRows.map((row) => (
                <React.Fragment key={row.id}>
                  <ColumnRow
                    $dragging={dragIndex === row.index}
                    draggable={!isSearching && !row.column.parentId}
                    onDragStart={() => setDragIndex(row.index)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => handleDrop(row.index)}
                    onDragEnd={() => setDragIndex(null)}
                  >
                    {row.column.parentId && <TreeConnector $isLast />}
                    {!isSearching && !row.column.parentId && (
                      <DragHandle>
                        <GripVertical size={14} />
                      </DragHandle>
                    )}
                    <ColumnCheckbox checked={!hiddenSet.has(row.id)} onChange={() => toggleColumn(row.id)} />
                    <ColumnName>{resolveColumnLabel(row.column)}</ColumnName>
                    {!isSearching && !row.column.parentId && (
                      <ArrowGroup>
                        <ArrowButton
                          type="button"
                          disabled={row.index === 0}
                          onClick={() => moveColumn(row.index, row.index - 1)}
                        >
                          <ArrowUp size={13} />
                        </ArrowButton>
                        <ArrowButton
                          type="button"
                          disabled={row.index === topLevelOrder.length - 1}
                          onClick={() => moveColumn(row.index, row.index + 1)}
                        >
                          <ArrowDown size={13} />
                        </ArrowButton>
                      </ArrowGroup>
                    )}
                  </ColumnRow>
                  {!isSearching && (childrenByParent.get(row.id) ?? []).map((child, childIndex, children) => (
                    <ColumnRow key={child.id} $dragging={false}>
                      <TreeConnector $isLast={childIndex === children.length - 1} />
                      <ColumnCheckbox checked={!hiddenSet.has(child.id)} onChange={() => toggleColumn(child.id)} />
                      <ColumnName>{resolveColumnLabel(child)}</ColumnName>
                    </ColumnRow>
                  ))}
                </React.Fragment>
              ))}
            </ColumnsList>
          </ColumnsPopover>,
          document.body,
        )}
    </ColumnsWrapper>
  )
}

export default ColumnSettings
